import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { bootstrapSetPassword, runSacredBootstrap } from "@/lib/sacred-library/sacred-library-api";
import { KeyRound, Loader2 } from "lucide-react";

/**
 * One-time login fix on /admin/sacred — uses Worker ADMIN_API_KEY, no console needed.
 */
export function BootstrapSetup({ onSuccess }) {
  const [adminKey, setAdminKey] = useState(() => {
    try {
      return localStorage.getItem("vedaPath_admin_key") || "";
    } catch {
      return "";
    }
  });
  const [email, setEmail] = useState("contact.vedapath@gmail.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const saveKey = () => {
    localStorage.setItem("vedaPath_admin_key", adminKey.trim());
    localStorage.setItem("vedapath_admin", "true");
  };

  const onSetup = async () => {
    if (!adminKey.trim()) {
      setMsg("Paste your ADMIN_API_KEY from Cloudflare Worker secrets.");
      return;
    }
    if (!password || password.length < 8) {
      setMsg("Choose a password (at least 8 characters).");
      return;
    }
    setBusy(true);
    setMsg("");
    saveKey();
    try {
      await runSacredBootstrap(email.trim(), password, adminKey.trim());
      setMsg("Admin created. Sign in below with this email and password.");
      onSuccess?.();
    } catch (e1) {
      const status = e1?.response?.status;
      if (status === 409) {
        try {
          await bootstrapSetPassword(email.trim(), password, adminKey.trim());
          setMsg("Password updated. Sign in below with this email and password.");
          onSuccess?.();
        } catch (e2) {
          setMsg(e2?.response?.data?.detail || "Set password failed — check ADMIN_API_KEY.");
        }
      } else {
        setMsg(e1?.response?.data?.detail || "Bootstrap failed — check ADMIN_API_KEY and deploy.");
      }
    }
    setBusy(false);
  };

  return (
    <Card className="mb-6 border-primary/30" data-testid="bootstrap-setup">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <KeyRound className="w-5 h-5" /> One-time login setup
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 max-w-md">
        <p className="text-sm text-muted-foreground">
          Do this once on <strong>vedapath.in/admin/sacred</strong> only if you use the legacy editor password login.
          If you sign in with a Worker JWT as admin/staff, skip this section — use{" "}
          <strong>/library</strong> or the tabs below instead.
        </p>
        <p className="text-xs text-muted-foreground">
          The Cloudflare secret <code className="text-xs">ADMIN_API_KEY</code> is for one-time bootstrap, not your site login.
        </p>
        <div>
          <Label>Admin API Key</Label>
          <Input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Same value as wrangler ADMIN_API_KEY"
          />
        </div>
        <div>
          <Label>Admin email</Label>
          <Input value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        <div>
          <Label>New password (you will use this to sign in)</Label>
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </div>
        <Button onClick={onSetup} disabled={busy} data-testid="bootstrap-setup-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
          Set password & enable login
        </Button>
        {msg && (
          <p className={`text-sm ${msg.includes("Sign in") ? "text-green-700" : "text-destructive"}`}>{msg}</p>
        )}
        <p className="text-xs text-muted-foreground">
          If an older account exists, try signing in as <strong>admin@vedapath.in</strong> /{" "}
          <strong>vedapath-admin</strong> first.
        </p>
      </CardContent>
    </Card>
  );
}
